import { create } from 'zustand';
import {
  Connection,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  addEdge,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  applyNodeChanges,
  applyEdgeChanges,
} from '@xyflow/react';
import { AgentOutfield, AgentInfield } from './api';

export type AppNode = Node;
export type AppEdge = Edge;

export type WorkflowState = {
  nodes: AppNode[];
  edges: AppEdge[];
  validationErrors: Record<string, string>;
  outfieldsMap: Record<string, AgentOutfield[]>;
  infieldsMap: Record<string, AgentInfield[]>;
  selectedId: string | null;
  inspectorOpen: boolean;
  onNodesChange: OnNodesChange<AppNode>;
  onEdgesChange: OnEdgesChange<AppEdge>;
  onConnect: OnConnect;
  setNodes: (nodes: AppNode[]) => void;
  setEdges: (edges: AppEdge[]) => void;
  updateNodeData: (nodeId: string, data: any) => void;
  updateEdgeData: (edgeId: string, data: any) => void;
  setValidationErrors: (errors: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
  setOutfieldsMap: (map: Record<string, AgentOutfield[]> | ((prev: Record<string, AgentOutfield[]>) => Record<string, AgentOutfield[]>)) => void;
  setInfieldsMap: (map: Record<string, AgentInfield[]> | ((prev: Record<string, AgentInfield[]>) => Record<string, AgentInfield[]>)) => void;
  setSelectedId: (id: string | null) => void;
  setInspectorOpen: (open: boolean) => void;
};

export const useWorkflowStore = create<WorkflowState>((set, get) => ({
  nodes: [],
  edges: [],
  validationErrors: {},
  outfieldsMap: {},
  infieldsMap: {},
  selectedId: null,
  inspectorOpen: false,
  
  onNodesChange: (changes: NodeChange<AppNode>[]) => {
    set({
      nodes: applyNodeChanges(changes, get().nodes),
    });
  },
  onEdgesChange: (changes: EdgeChange<AppEdge>[]) => {
    set({
      edges: applyEdgeChanges(changes, get().edges),
    });
  },
  onConnect: (connection: Connection) => {
    const edges = get().edges;
    const existingFromSource = edges.filter(e => e.source === connection.source);
    const nextPriority = existingFromSource.length > 0 ? Math.max(...existingFromSource.map(e => (e.data?.priority as number) || 0)) + 1 : 0;
    
    const newEdge: AppEdge = {
      id: `${connection.source}-${connection.target}`,
      source: connection.source,
      target: connection.target,
      type: 'conditionEdge',
      data: {
        priority: nextPriority,
        conditions: [{ field: "", operator: "equals", value: "" }],
        delay: null,
      }
    };
    
    set({
      edges: addEdge(newEdge, get().edges),
    });
  },
  setNodes: (nodes: AppNode[]) => {
    set({ nodes });
  },
  setEdges: (edges: AppEdge[]) => {
    set({ edges });
  },
  updateNodeData: (nodeId: string, data: any) => {
    set({
      nodes: get().nodes.map((node) => {
        if (node.id === nodeId) {
          return { ...node, data: { ...node.data, ...data } };
        }
        return node;
      }),
    });
    // Clear validation error if exists
    const errors = { ...get().validationErrors };
    if (errors[nodeId]) {
      delete errors[nodeId];
      set({ validationErrors: errors });
    }
  },
  updateEdgeData: (edgeId: string, data: any) => {
    set({
      edges: get().edges.map((edge) => {
        if (edge.id === edgeId) {
          return { ...edge, data: { ...edge.data, ...data } };
        }
        return edge;
      }),
    });
    // Clear validation error if exists
    const errors = { ...get().validationErrors };
    if (errors[edgeId]) {
      delete errors[edgeId];
      set({ validationErrors: errors });
    }
  },
  setValidationErrors: (updater) => {
    set((state) => ({
      validationErrors: typeof updater === 'function' ? updater(state.validationErrors) : updater
    }));
  },
  setOutfieldsMap: (updater) => {
    set((state) => ({
      outfieldsMap: typeof updater === 'function' ? updater(state.outfieldsMap) : updater
    }));
  },
  setInfieldsMap: (updater) => {
    set((state) => ({
      infieldsMap: typeof updater === 'function' ? updater(state.infieldsMap) : updater
    }));
  },
  setSelectedId: (id: string | null) => set({ selectedId: id }),
  setInspectorOpen: (open: boolean) => set({ inspectorOpen: open }),
}));
